import { useEffect, useMemo, useRef, useState } from 'react'
import { RequiredAsterisk } from '../required-asterisk'
import { notifyDropdownOpen } from '../../../hooks/use-cached-fetch'

interface FormComboBoxProps {
    label: string
    keyName: string
    value: string
    options: string[]
    onChangeValue: (value: string) => void
    isRequired?: boolean
    className?: string
    placeholder?: string
    isDisabled?: boolean
    dropdownRefreshScope?: string
}

export const FormComboBox: React.FC<FormComboBoxProps> = ({
    label,
    keyName,
    value,
    options,
    onChangeValue,
    isRequired = true,
    className = '',
    placeholder,
    isDisabled = false,
    dropdownRefreshScope,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)

    const normalizedValue = value.trim().toLowerCase()
    const uniqueOptions = useMemo(() => Array.from(new Set(options)).filter(Boolean).sort((a, b) => a.localeCompare(b)), [options])
    const filteredOptions = useMemo(() => {
        if (!normalizedValue) return uniqueOptions
        return uniqueOptions.filter(option => option.toLowerCase().includes(normalizedValue))
    }, [normalizedValue, uniqueOptions])

    useEffect(() => {
        if (!isOpen) return
        const handleMouseDown = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleMouseDown)
        return () => document.removeEventListener('mousedown', handleMouseDown)
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsOpen(false)
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [isOpen])

    const openMenu = () => {
        if (isDisabled) return
        if (!isOpen) notifyDropdownOpen(dropdownRefreshScope)
        setIsOpen(true)
    }

    const toggleMenu = () => {
        if (isDisabled) return
        if (!isOpen) notifyDropdownOpen(dropdownRefreshScope)
        setIsOpen(prev => !prev)
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChangeValue(event.target.value)
        if (!isDisabled) openMenu()
    }

    const handleSelect = (selectedValue: string) => {
        onChangeValue(selectedValue)
        setIsOpen(false)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            openMenu()
            return
        }

        if (event.key === 'Enter' && isOpen && filteredOptions.length > 0) {
            event.preventDefault()
            handleSelect(filteredOptions[0])
        }
    }

    return (
        <div className={`form-combobox ${className}`} ref={wrapperRef}>
            <div className="form-combobox__labelRow">
                <label htmlFor={keyName}>{label}</label>
                {isRequired && <RequiredAsterisk />}
            </div>
            <div className="form-combobox__control">
                <input
                    id={keyName}
                    name={keyName}
                    type="text"
                    role="combobox"
                    value={value}
                    onChange={handleChange}
                    onFocus={openMenu}
                    onKeyDown={handleKeyDown}
                    disabled={isDisabled}
                    placeholder={placeholder}
                    autoComplete="off"
                    aria-autocomplete="list"
                    aria-expanded={isOpen}
                    aria-controls={`${keyName}-menu`}
                    aria-haspopup="listbox"
                />
                <button
                    type="button"
                    className="form-combobox__toggle"
                    onClick={toggleMenu}
                    disabled={isDisabled}
                    aria-label={`Show ${label} suggestions`}
                    aria-expanded={isOpen}
                >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>

                {isOpen && filteredOptions.length > 0 && (
                    <div className="form-combobox__menu" id={`${keyName}-menu`} role="listbox">
                        {filteredOptions.map(option => (
                            <button
                                key={option}
                                type="button"
                                className={`form-combobox__item ${option === value ? 'is-selected' : ''}`}
                                onClick={() => handleSelect(option)}
                                role="option"
                                aria-selected={option === value}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                )}

                {isOpen && filteredOptions.length === 0 && normalizedValue && (
                    <div className="form-combobox__menu" id={`${keyName}-menu`} role="listbox">
                        <div className="form-combobox__empty">No matches</div>
                    </div>
                )}
            </div>
        </div>
    )
}
